import React, { useState, useRef, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

import { IoChevronDownSharp, IoChevronUp } from "react-icons/io5";
import { FaAngleRight, FaAngleLeft } from "react-icons/fa6";

import { cn } from "../hooks/lib/utils";
import { FomatText } from "../utils/formatText";


const DataTable = ({
  columns,
  data = [],
  loading = false,
  pagination,
  onRowClick,
  onSort,
  className,
  emptyMessage = "No data available",
  showCheckbox = false,
  selectedRows = { enrollments: [] },
  onCheck = () => {},
}) => {
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("");
  const headerCheckboxRef = useRef(null);

  const TotalPage =
    pagination?.totalPage / 20 > 1 && pagination?.total_pages > 1
      ? pagination?.total_pages
      : pagination?.totalPage / 20 < 1
      ? 1
      : Math.round(pagination?.totalPage / 20);

  // 🟡 Fix: Header checkbox indeterminate logic
  useEffect(() => {
    if (!headerCheckboxRef.current) return;

    const pageRowIds = data.map((r) => r.Membership_id);

    const selectedOnPage = pageRowIds.filter((id) =>
      selectedRows.enrollments.some((r) => r.Membership_id === id)
    ).length;

    console.log(selectedRows);

    headerCheckboxRef.current.indeterminate =
      selectedOnPage > 0 && selectedOnPage < pageRowIds.length;
  }, [data, selectedRows]);

  const handleSort = (column) => {
    if (!column.sortable) return;

    const newDirection =
      sortColumn === column.key && sortDirection === "asc" ? "desc" : "asc";
    setSortColumn(column.key);
    setSortDirection(newDirection);

    if (onSort) {
      onSort(column.key, newDirection);
    }
  };

  const renderSortIcon = (column) => {
    if (!column.sortable) return null;

    if (sortColumn !== column.key) {
      return <IoChevronDownSharp className="ml-2 h-4 w-4 opacity-50" />;
    }

    return sortDirection === "asc" ? (
      <IoChevronUp className="ml-2 h-4 w-4" />
    ) : (
      <IoChevronDownSharp className="ml-2 h-4 w-4" />
    );
  };

  if (loading) {
    return <TableSkeleton columns={columns} showCheckbox={showCheckbox} />;
  }

  const goToPage = (page) => {
    if (page >= 1 && page <= TotalPage) {
      pagination?.setPage(page);
    }
  };

  const pageRowIds = data.map((r) => r.Membership_id);

  // 🟡 Fix: Select all checkbox uses .some()
  const isAllSelectedOnPage =
    pageRowIds.length > 0 &&
    pageRowIds.every((id) =>
      selectedRows.enrollments.some((r) => r.Membership_id === id)
    );

  return (
    <div className={cn("w-full", className)}>
      <div className="rounded-md border bg-white">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                {showCheckbox && (
                  <TableHead className="py-4 px-6">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={isAllSelectedOnPage}
                      onChange={(e) => onCheck("all", e.target.checked)}
                    />
                  </TableHead>
                )}

                {columns.map((column) => (
                  <TableHead
                    key={column.key}
                    className={cn(
                      "font-medium text-gray-700 py-4 px-6",
                      column.sortable && "cursor-pointer hover:bg-gray-100",
                      column.className
                    )}
                    onClick={() => handleSort(column)}
                  >
                    <div className="flex items-center">
                      {column.header}
                      {renderSortIcon(column)}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {data.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + (showCheckbox ? 1 : 0)}
                    className="h-24 text-center text-gray-500"
                  >
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row, index) => (
                  <TableRow
                    key={index}
                    className={cn(
                      "border-b hover:bg-gray-50 transition-colors",
                      onRowClick && "cursor-pointer",

                      // 🟡 Fix: Highlight selected row
                      selectedRows.enrollments.some(
                        (r) => r.Membership_id === row.Membership_id
                      ) && "bg-yellow-50"
                    )}
                    onClick={() => onRowClick(row)}
                  >
                    {showCheckbox && (
                      <TableCell className="py-4 px-6">
                        <input
                          type="checkbox"
                          checked={selectedRows.enrollments.some(
                            (r) => r.Membership_id === row.Membership_id
                          )}
                          onChange={(e) =>
                            onCheck(row.Membership_id, e.target.checked)
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                    )}

                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        className={cn(
                          "py-4 px-6 text-gray-700",
                          column.className
                        )}
                      >
                        {column.render
                          ? column.render(row[column.key], row)
                          : typeof row[column.key] === "string"
                          ? FomatText(row[column.key])
                          : !row[column.key]
                          ? "-"
                          : row[column.key]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center w-[154px] justify-between mt-3 text-center p-2">
        <button
          disabled={pagination?.currentPage === 1}
          onClick={() => goToPage(pagination?.currentPage - 1)}
          className="cursor-pointer disabled:opacity-50"
        >
          <FaAngleLeft />
        </button>

        {Array.from({ length: Math.min(TotalPage, 3) }, (_, i) => {
          const page = i + 1;
          return (
            <button
              key={page}
              onClick={() => goToPage(page)}
              className={`h-[33px] w-[28px] flex items-center justify-center rounded-md cursor-pointer 
                ${
                  pagination?.currentPage === page
                    ? "bg-brandYellow-800 text-white p-2"
                    : ""
                }`}
            >
              {page}
            </button>
          );
        })}

        {TotalPage > 3 && (
          <>
            <span className="px-2">...</span>
            <button
              onClick={() => goToPage(TotalPage)}
              className={`h-[33px] w-[28px] flex items-center justify-center rounded-md cursor-pointer
                ${
                  pagination?.currentPage === TotalPage
                    ? "bg-brandYellow-800 text-white p-3"
                    : ""
                }`}
            >
              {TotalPage}
            </button>
          </>
        )}

        <button
          disabled={pagination?.currentPage === TotalPage}
          onClick={() => goToPage(pagination?.currentPage + 1)}
          className="cursor-pointer disabled:opacity-50"
        >
          <FaAngleRight />
        </button>
      </div>
    </div>
  );
};

// const DataTable = ({
//   columns,
//   data = [],
//   loading = false,
//   pagination,
//   onRowClick,
//   onSort,
//   className,
//   emptyMessage = "No data available",
//   showCheckbox = false, // enable checkbox column
//   selectedRows = [], // array of selected row IDs (e.g. Membership_id)
//   onCheck = () => {}, // (rowId | "all", checked) => void
// }) => {
//   const [sortColumn, setSortColumn] = useState("");
//   const [sortDirection, setSortDirection] = useState("");
//   const headerCheckboxRef = useRef(null);

//   const TotalPage =
//     pagination?.totalPage / 20 > 1 && pagination?.total_pages > 1
//       ? pagination?.total_pages
//       : pagination?.totalPage / 20 < 1
//       ? 1
//       : Math.round(pagination?.totalPage / 20);

//   // Header checkbox indeterminate logic
//   useEffect(() => {
//     if (!headerCheckboxRef.current) return;
//     const pageRowIds = data.map((r) => r.Membership_id);
//     const selectedOnPage = pageRowIds.filter((id) =>
//       selectedRows.includes(id)
//     ).length;

//     headerCheckboxRef.current.indeterminate =
//       selectedOnPage > 0 && selectedOnPage < pageRowIds.length;
//   }, [data, selectedRows]);

//   console.log(selectedRows);

//   const handleSort = (column) => {
//     if (!column.sortable) return;

//     const newDirection =
//       sortColumn === column.key && sortDirection === "asc" ? "desc" : "asc";
//     setSortColumn(column.key);
//     setSortDirection(newDirection);

//     if (onSort) {
//       onSort(column.key, newDirection);
//     }
//   };

//   const renderSortIcon = (column) => {
//     if (!column.sortable) return null;

//     if (sortColumn !== column.key) {
//       return <IoChevronDownSharp className="ml-2 h-4 w-4 opacity-50" />;
//     }

//     return sortDirection === "asc" ? (
//       <IoChevronUp className="ml-2 h-4 w-4" />
//     ) : (
//       <IoChevronDownSharp className="ml-2 h-4 w-4" />
//     );
//   };

//   if (loading) {
//     return <TableSkeleton columns={columns} showCheckbox={showCheckbox} />;
//   }

//   const goToPage = (page) => {
//     if (page >= 1 && page <= TotalPage) {
//       pagination?.setPage(page);
//     }
//   };

//   const pageRowIds = data.map((r) => r.Membership_id);
//   const isAllSelectedOnPage =
//     pageRowIds.length > 0 &&
//     pageRowIds.every((id) => selectedRows.includes(id));

//   return (
//     <div className={cn("w-full", className)}>
//       <div className="rounded-md border bg-white">
//         <div className="overflow-x-auto">
//           <Table>
//             <TableHeader>
//               <TableRow className="bg-gray-50">
//                 {showCheckbox && (
//                   <TableHead className="py-4 px-6">
//                     <input
//                       ref={headerCheckboxRef}
//                       type="checkbox"
//                       checked={isAllSelectedOnPage}
//                       onChange={(e) =>
//                         onCheck && onCheck("all", e.target.checked)
//                       }
//                     />
//                   </TableHead>
//                 )}
//                 {columns.map((column) => (
//                   <TableHead
//                     key={column.key}
//                     className={cn(
//                       "font-medium text-gray-700 py-4 px-6",
//                       column.sortable && "cursor-pointer hover:bg-gray-100",
//                       column.className
//                     )}
//                     onClick={() => handleSort(column)}
//                   >
//                     <div className="flex items-center">
//                       {column.header}
//                       {renderSortIcon(column)}
//                     </div>
//                   </TableHead>
//                 ))}
//               </TableRow>
//             </TableHeader>

//             <TableBody>
//               {data.length === 0 ? (
//                 <TableRow>
//                   <TableCell
//                     colSpan={columns.length + (showCheckbox ? 1 : 0)}
//                     className="h-24 text-center text-gray-500"
//                   >
//                     {emptyMessage}
//                   </TableCell>
//                 </TableRow>
//               ) : (
//                 data.map((row, index) => (
//                   <TableRow
//                     key={index}
//                     className={cn(
//                       "border-b hover:bg-gray-50 transition-colors",
//                       onRowClick && "cursor-pointer",
//                       selectedRows.includes(row.Membership_id) &&
//                         "bg-yellow-50"
//                     )}
//                     onClick={() => onRowClick && onRowClick(row)}
//                   >
//                     {showCheckbox && (
//                       <TableCell className="py-4 px-6">
//                         <input
//                           type="checkbox"
//                           checked={selectedRows.includes(row.Membership_id)}
//                           onChange={(e) =>
//                             onCheck &&
//                             onCheck(row.Membership_id, e.target.checked)
//                           }
//                           onClick={(e) => e.stopPropagation()}
//                         />
//                       </TableCell>
//                     )}

//                     {columns.map((column) => (
//                       <TableCell
//                         key={column.key}
//                         className={cn("py-4 px-6 text-gray-700", column.className)}
//                       >
//                         {column.render
//                           ? column.render(row[column.key], row)
//                           : typeof row[column.key] === "string"
//                           ? FomatText(row[column.key])
//                           : !row[column.key]
//                           ? "-"
//                           : row[column.key]}
//                       </TableCell>
//                     ))}
//                   </TableRow>
//                 ))
//               )}
//             </TableBody>
//           </Table>
//         </div>
//       </div>

//       {/* Pagination */}
//       <div className="flex items-center w-[154px] justify-between mt-3 text-center p-2">
//         <button
//           disabled={pagination?.currentPage === 1}
//           onClick={() => goToPage(pagination?.currentPage - 1)}
//           className="cursor-pointer disabled:opacity-50"
//         >
//           <FaAngleLeft />
//         </button>

//         {Array.from({ length: Math.min(TotalPage, 3) }, (_, i) => {
//           const page = i + 1;
//           return (
//             <button
//               key={page}
//               onClick={() => goToPage(page)}
//               className={`h-[33px] w-[28px] flex items-center justify-center rounded-md cursor-pointer 
//                 ${
//                   pagination?.currentPage === page
//                     ? "bg-brandYellow-800 text-white p-2"
//                     : ""
//                 }`}
//             >
//               {page}
//             </button>
//           );
//         })}

//         {TotalPage > 3 && (
//           <>
//             <span className="px-2">...</span>
//             <button
//               onClick={() => goToPage(TotalPage)}
//               className={`h-[33px] w-[28px] flex items-center justify-center rounded-md cursor-pointer
//                 ${
//                   pagination?.currentPage === TotalPage
//                     ? "bg-brandYellow-800 text-white p-3"
//                     : ""
//                 }`}
//             >
//               {TotalPage}
//             </button>
//           </>
//         )}

//         <button
//           disabled={pagination?.currentPage === TotalPage}
//           onClick={() => goToPage(pagination?.currentPage + 1)}
//           className="cursor-pointer disabled:opacity-50"
//         >
//           <FaAngleRight />
//         </button>
//       </div>
//     </div>
//   );
// };

const TableSkeleton = ({ columns, showCheckbox = false }) => {
  return (
    <div className="w-full rounded-md border bg-white">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              {showCheckbox && (
                <TableHead className="py-4 px-6">
                  <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                </TableHead>
              )}
              {columns.map((column) => (
                <TableHead key={column.key} className="py-4 px-6">
                  <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, index) => (
              <TableRow key={index}>
                {showCheckbox && (
                  <TableCell className="py-4 px-6">
                    <div className="h-4 bg-gray-100 rounded animate-pulse"></div>
                  </TableCell>
                )}
                {columns.map((column) => (
                  <TableCell key={column.key} className="py-4 px-6">
                    <div className="h-4 bg-gray-100 rounded animate-pulse"></div>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default DataTable;




// import React, { useState, useRef, useEffect } from "react";
// import {
//   Table,
//   TableBody,
//   TableCell,
//   TableHead,
//   TableHeader,
//   TableRow,
// } from "../components/ui/table";

// import { IoChevronDownSharp, IoChevronUp } from "react-icons/io5";
// import { FaAngleRight, FaAngleLeft } from "react-icons/fa6";

// import { cn } from "../hooks/lib/utils";
// import { FomatText } from "../utils/formatText";

// const DataTable = ({
//   columns,
//   data = [],
//   loading = false,
//   pagination,
//   onRowClick,
//   onSort,
//   className,
//   emptyMessage = "No data available",
//   showCheckbox = false, // enable checkbox column
//   selectedRows = [], // array of selected row IDs (e.g. Membership_id)
//   onCheck = () => {}, // (rowId | "all", checked) => void
// }) => {
//   const [sortColumn, setSortColumn] = useState("");
//   const [sortDirection, setSortDirection] = useState("");
//   const headerCheckboxRef = useRef(null);

//   const TotalPage =
//     pagination?.totalPage / 20 > 1 && pagination?.total_pages > 1
//       ? pagination?.total_pages
//       : pagination?.totalPage / 20 < 1
//       ? 1
//       : Math.round(pagination?.totalPage / 20);

//   useEffect(() => {
//     // set indeterminate state on header checkbox when some but not all rows are selected
//     if (!headerCheckboxRef.current) return;
//     const pageRowIds = data.map((r) => r.Membership_id);
//     const selectedOnPage = pageRowIds.filter((id) =>
//       selectedRows.includes(id)
//     ).length;

//     headerCheckboxRef.current.indeterminate =
//       selectedOnPage > 0 && selectedOnPage < pageRowIds.length;
//   }, [data, selectedRows]);
  
//   console.log(selectedRows);

//   const handleSort = (column) => {
//     if (!column.sortable) return;

//     const newDirection =
//       sortColumn === column.key && sortDirection === "asc" ? "desc" : "asc";
//     setSortColumn(column.key);
//     setSortDirection(newDirection);

//     if (onSort) {
//       onSort(column.key, newDirection);
//     }
//   };

//   const renderSortIcon = (column) => {
//     if (!column.sortable) return null;

//     if (sortColumn !== column.key) {
//       return <IoChevronDownSharp className="ml-2 h-4 w-4 opacity-50" />;
//     }

//     return sortDirection === "asc" ? (
//       <IoChevronUp className="ml-2 h-4 w-4" />
//     ) : (
//       <IoChevronDownSharp className="ml-2 h-4 w-4" />
//     );
//   };

//   if (loading) {
//     return <TableSkeleton columns={columns} showCheckbox={showCheckbox} />;
//   }

//   const goToPage = (page) => {
//     if (page >= 1 && page <= TotalPage) {
//       pagination?.setPage(page);
//     }
//   };

//   // helper to get page IDs (used for header select-all)
//   const pageRowIds = data.map((r) => r.Membership_id);

//   const isAllSelectedOnPage =
//     pageRowIds.length > 0 &&
//     pageRowIds.every((id) => selectedRows.includes(id));
  

//   return (
//     <div className={cn("w-full", className)}>
//       <div className="rounded-md border bg-white">
//         <div className="overflow-x-auto">
//           <Table>
//             <TableHeader>
//               <TableRow className="bg-gray-50">
//                 {showCheckbox && (
//                   <TableHead className="py-4 px-6">
//                     <input
//                       ref={headerCheckboxRef}
//                       type="checkbox"
//                       checked={isAllSelectedOnPage}
//                       onChange={(e) => onCheck && onCheck("all", e.target.checked)}
//                     />
//                   </TableHead>
//                 )}

//                 {columns.map((column) => (
//                   <TableHead
//                     key={column.key}
//                     className={cn(
//                       "font-medium text-gray-700 py-4 px-6",
//                       column.sortable && "cursor-pointer hover:bg-gray-100",
//                       column.className
//                     )}
//                     onClick={() => handleSort(column)}
//                   >
//                     <div className="flex items-center">
//                       {column.header}
//                       {renderSortIcon(column)}
//                     </div>
//                   </TableHead>
//                 ))}
//               </TableRow>
//             </TableHeader>

//             <TableBody>
//               {data.length === 0 ? (
//                 <TableRow>
//                   <TableCell
//                     colSpan={columns.length + (showCheckbox ? 1 : 0)}
//                     className="h-24 text-center text-gray-500"
//                   >
//                     {emptyMessage}
//                   </TableCell>
//                 </TableRow>
//               ) : (
//                 data.map((row, index) => (
//                   <TableRow
//                     key={index}
//                     className={cn(
//                       "border-b hover:bg-gray-50 transition-colors",
//                       onRowClick && "cursor-pointer",
//                       selectedRows.includes(row.Membership_id) && "bg-yellow-50"
//                     )}
//                     onClick={() => onRowClick && onRowClick(row)}
//                   >
//                     {showCheckbox && (
//                       <TableCell className="py-4 px-6">
//                         <input
//                           type="checkbox"
//                           checked={selectedRows.includes(row.Membership_id)}
//                           onChange={(e) =>
//                             onCheck && onCheck(row.Membership_id, e.target.checked)
//                           }
//                           onClick={(e) => e.stopPropagation()}
//                         />
//                       </TableCell>
//                     )}

//                     {columns.map((column) => (
//                       <TableCell
//                         key={column.key}
//                         className={cn("py-4 px-6 text-gray-700", column.className)}
//                       >
//                         {column.render
//                           ? column.render(row[column.key], row)
//                           : typeof row[column.key] === "string"
//                           ? FomatText(row[column.key])
//                           : !row[column.key]
//                           ? "-"
//                           : row[column.key]}
//                       </TableCell>
//                     ))}
//                   </TableRow>
//                 ))
//               )}
//             </TableBody>
//           </Table>
//         </div>
//       </div>

//       {/* Pagination */}
//       <div className="flex items-center w-[154px] justify-between mt-3 text-center p-2">
//         <button
//           disabled={pagination?.currentPage === 1}
//           onClick={() => goToPage(pagination?.currentPage - 1)}
//           className="cursor-pointer disabled:opacity-50"
//         >
//           <FaAngleLeft />
//         </button>

//         {Array.from({ length: Math.min(TotalPage, 3) }, (_, i) => {
//           const page = i + 1;
//           return (
//             <button
//               key={page}
//               onClick={() => goToPage(page)}
//               className={`h-[33px] w-[28px] flex items-center justify-center rounded-md cursor-pointer 
//                 ${
//                   pagination?.currentPage === page
//                     ? "bg-brandYellow-800 text-white p-2"
//                     : ""
//                 }`}
//             >
//               {page}
//             </button>
//           );
//         })}

//         {TotalPage > 3 && (
//           <>
//             <span className="px-2">...</span>
//             <button
//               onClick={() => goToPage(TotalPage)}
//               className={`h-[33px] w-[28px] flex items-center justify-center rounded-md cursor-pointer
//                 ${
//                   pagination?.currentPage === TotalPage
//                     ? "bg-brandYellow-800 text-white p-3"
//                     : ""
//                 }`}
//             >
//               {TotalPage}
//             </button>
//           </>
//         )}

//         <button
//           disabled={pagination?.currentPage === TotalPage}
//           onClick={() => goToPage(pagination?.currentPage + 1)}
//           className="cursor-pointer disabled:opacity-50"
//         >
//           <FaAngleRight />
//         </button>
//       </div>
//     </div>
//   );
// };

// const TableSkeleton = ({ columns, showCheckbox = false }) => {
//   return (
//     <div className="w-full rounded-md border bg-white">
//       <div className="overflow-x-auto">
//         <Table>
//           <TableHeader>
//             <TableRow className="bg-gray-50">
//               {showCheckbox && <TableHead className="py-4 px-6"><div className="h-4 bg-gray-200 rounded animate-pulse"></div></TableHead>}
//               {columns.map((column) => (
//                 <TableHead key={column.key} className="py-4 px-6">
//                   <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
//                 </TableHead>
//               ))}
//             </TableRow>
//           </TableHeader>
//           <TableBody>
//             {Array.from({ length: 5 }).map((_, index) => (
//               <TableRow key={index}>
//                 {showCheckbox && <TableCell className="py-4 px-6"><div className="h-4 bg-gray-100 rounded animate-pulse"></div></TableCell>}
//                 {columns.map((column) => (
//                   <TableCell key={column.key} className="py-4 px-6">
//                     <div className="h-4 bg-gray-100 rounded animate-pulse"></div>
//                   </TableCell>
//                 ))}
//               </TableRow>
//             ))}
//           </TableBody>
//         </Table>
//       </div>
//     </div>
//   );
// };

// export default DataTable;
